// FirstUserRegistrationPage - bootstrap setup shown when the orchestrator
// reports needs_bootstrap. Creates the first admin user + org + manager
// membership and sets a session cookie. Once any admin exists the endpoint
// 409s (App.tsx normally blocks this path; the page surfaces the error anyway).

import { ArrowRight, Loader2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthShell } from '@/components/AuthShell';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { ApiError, api } from '@/lib/api';

export default function FirstUserRegistrationPage() {
  const { refresh } = useAuth();
  const toast = useToast();
  const nav = useNavigate();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (password.length < 12) {
      toast.error('Password must be at least 12 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await api.bootstrapFirstUser({
        email: email.trim(),
        display_name: fullName.trim(),
        password,
        org_name: orgName.trim(),
      });
      // Bootstrap endpoint set the session cookie; refresh the AuthContext
      // so it picks up the new user without a hard reload.
      await refresh();
      toast.success(`Welcome, ${fullName || email}.`);
      nav('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          toast.error('Setup already complete. Please sign in.');
          nav('/login', { replace: true });
          return;
        }
        toast.error(err.message || 'Setup failed');
      } else {
        toast.error('Setup failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell subtitle="First-time setup">
      <div className="card p-6 sm:p-7">
        <div className="mb-5">
          <h1 className="text-lg font-semibold tracking-tight text-ink-50">
            Create the first admin
          </h1>
          <p className="text-xs text-ink-500 mt-1">
            This is the system's initial setup. The account you create here is
            the administrator and the manager of your first organization.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-3.5">
          <div>
            <label className="label" htmlFor="fullName">Your name</label>
            <input
              id="fullName"
              type="text"
              autoComplete="name"
              required
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="orgName">Organization name</label>
            <input
              id="orgName"
              type="text"
              autoComplete="organization"
              required
              className="input"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
            <div className="help-text">
              The name of your system administrators' organization. You'll be its manager.
            </div>
          </div>

          <div>
            <label className="label" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="confirmPassword">Confirm password</label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="btn-primary w-full !mt-5 group"
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Create account
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </form>
      </div>
    </AuthShell>
  );
}
